import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Loader } from "@/components";
import { TextArea } from "@/components/TextArea/TextArea";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useMediaJobSocket } from "@/pages/Private/helpers/useMediaJobSocket";
import {
	useCancelMediaJobMutation,
	useCreatePhaseImageJobMutation,
	useEstimateMediaCostMutation,
	useGetAiModelsQuery,
	useGetFormatPresetsQuery,
	useGetMediaJobQuery,
	useSelectMediaJobAssetMutation,
} from "@/pages/Private/redux/media/media.api";
import {
	AiModelUseCase,
	MediaEstimate,
	MediaJobStatus,
	MediaKind,
	QualityTier,
	TERMINAL_JOB_STATUSES,
} from "@/pages/Private/redux/media/media.schema";
import { classNames } from "@/utils/classNames";

const MAX_PROMPT_LENGTH = 2000;

interface AiImagePanelProps {
	courseId: number;
	phaseDataId?: number;
	/** Called with the course-file URL once the author picks a variant. */
	onImageSelected: (fullFilePath: string) => void;
}

const formatUsd = (amount: number) =>
	`$${amount.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * The AI alternative to uploading a phase image. Mirrors the TTS panel's shape: the author
 * describes what they want, the server prices it, one asynchronous job renders every
 * selected format, and picking a variant writes it into phase content.
 *
 * No price is ever computed here — `POST /media/estimate` is the only source of a figure,
 * because arithmetic duplicated in the client drifts the moment the registry changes.
 */
export const AiImagePanel: FunctionComponent<AiImagePanelProps> = ({
	courseId,
	phaseDataId,
	onImageSelected,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`courses.aiImage.${key}`, options ?? {}),
		[t]
	);

	const [prompt, setPrompt] = useState("");
	const [qualityTier, setQualityTier] = useState<QualityTier>(QualityTier.BULK);
	const [selectedFormatIds, setSelectedFormatIds] = useState<number[]>([]);
	const [activeJobId, setActiveJobId] = useState<number | null>(null);
	const [previewFormatId, setPreviewFormatId] = useState<number | null>(null);
	const [estimate, setEstimate] = useState<MediaEstimate | null>(null);
	const [error, setError] = useState("");

	const { data: presets, isLoading: isLoadingPresets } = useGetFormatPresetsQuery({
		mediaKind: MediaKind.IMAGE,
	});
	const { data: models } = useGetAiModelsQuery({ useCase: AiModelUseCase.PHASE_IMAGE });
	const [estimateCost] = useEstimateMediaCostMutation();
	const [createJob, { isLoading: isSubmitting }] = useCreatePhaseImageJobMutation();
	const [cancelJob, { isLoading: isCancelling }] = useCancelMediaJobMutation();
	const [selectAsset, { isLoading: isSelecting }] = useSelectMediaJobAssetMutation();

	const { data: job, refetch: refetchJob } = useGetMediaJobQuery(activeJobId as number, {
		skip: activeJobId === null,
		// Sockets are an accelerator, not a guarantee; a focus refetch is the client-side
		// backstop, and the reconciliation cron is the server-side one.
		refetchOnFocus: true,
	});

	const tierModel = useMemo(
		() => (models ?? []).find((model) => model.qualityTier === qualityTier),
		[models, qualityTier]
	);
	const isRunning = job ? !TERMINAL_JOB_STATUSES.includes(job.status) : false;

	useMediaJobSocket({
		courseId,
		onJobUpdated: () => refetchJob(),
		onJobSucceeded: () => refetchJob(),
		onJobFailed: () => refetchJob(),
	});

	// Default to every image preset the company has, matching the mockup's two-of-three
	// preselection without hardcoding which ones exist.
	useEffect(() => {
		if (presets?.length && !selectedFormatIds.length) {
			setSelectedFormatIds(presets.slice(0, 2).map((preset) => preset.id));
		}
	}, [presets, selectedFormatIds.length]);

	useEffect(() => {
		if (!selectedFormatIds.length || !tierModel) {
			setEstimate(null);

			return;
		}

		let cancelled = false;

		estimateCost({
			useCase: AiModelUseCase.PHASE_IMAGE,
			qualityTier,
			quantities: {
				variants: tierModel.defaultVariantCount,
				formats: selectedFormatIds.length,
			},
		})
			.unwrap()
			.then((result) => {
				if (!cancelled) {
					setEstimate(result);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setEstimate(null);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [estimateCost, qualityTier, selectedFormatIds, tierModel]);

	const variantsForPreview = useMemo(() => {
		if (!job?.assets.length) {
			return [];
		}

		const formatId = previewFormatId ?? job.assets[0].formatPresetId ?? null;

		return job.assets.filter((asset) => (asset.formatPresetId ?? null) === formatId);
	}, [job, previewFormatId]);

	const toggleFormat = (id: number) =>
		setSelectedFormatIds((current) =>
			current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
		);

	const handleGenerate = async () => {
		setError("");

		if (!prompt.trim()) {
			setError(ts("promptRequired"));

			return;
		}

		if (!selectedFormatIds.length) {
			setError(ts("noFormatSelected"));

			return;
		}

		try {
			const created = await createJob({
				courseId,
				phaseDataId,
				prompt: prompt.trim(),
				qualityTier,
				formatPresetIds: selectedFormatIds,
			}).unwrap();

			setActiveJobId(created.id);
			setPreviewFormatId(null);
		} catch {
			setError(ts("submitError"));
		}
	};

	const handleSelectVariant = async (assetId: number) => {
		if (!job) {
			return;
		}

		setError("");

		try {
			const courseFile = await selectAsset({ jobId: job.id, assetId }).unwrap();

			if (courseFile.fullFilePath) {
				onImageSelected(courseFile.fullFilePath);
			}
		} catch {
			setError(ts("selectError"));
		}
	};

	const tierLabel = qualityTier === QualityTier.BULK ? ts("tierBulk") : ts("tierHero");

	return (
		<div className="space-y-4">
			{tierModel ? (
				<div className="flex flex-wrap gap-2">
					<span className="rounded-full bg-accent-tint px-2.5 py-1 text-xs font-semibold text-accent-strong">
						{tierModel.displayName}
					</span>
					{!tierModel.pricesVerified && (
						<span className="rounded-full bg-status-running-light px-2.5 py-1 text-xs font-semibold text-warning-ink">
							{ts("priceUnverified")}
						</span>
					)}
				</div>
			) : (
				<p className="text-xs text-subtle">{ts("noModelHint")}</p>
			)}

			<TextArea
				className="min-h-[120px] !border !border-border"
				handleChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
					setPrompt(event.target.value)
				}
				information={ts("promptHint")}
				label={ts("prompt")}
				maxLength={MAX_PROMPT_LENGTH}
				name="aiImagePrompt"
				placeholder={ts("promptPlaceholder")}
				showError={false}
				value={prompt}
			/>

			<div>
				<span className="mb-1.5 block text-xs font-bold text-ink">{ts("qualityTier")}</span>
				<div className="grid grid-cols-2 gap-2">
					{[QualityTier.BULK, QualityTier.HERO].map((tier) => {
						const model = (models ?? []).find((candidate) => candidate.qualityTier === tier);
						const isActive = qualityTier === tier;

						return (
							<button
								key={tier}
								className={classNames(
									"rounded-card border p-3 text-left transition-colors",
									isActive
										? "border-accent bg-accent-tint"
										: "border-border bg-white hover:bg-surface"
								)}
								type="button"
								onClick={() => setQualityTier(tier)}
							>
								<span className="block text-sm font-bold text-ink">
									{tier === QualityTier.BULK ? ts("tierBulk") : ts("tierHero")}
								</span>
								<span className="mt-1 block text-xs text-subtle">
									{model ? `~${formatUsd(model.unitPriceUsd)} ${ts("perImage")} · ` : ""}
									{tier === QualityTier.BULK ? ts("tierBulkHint") : ts("tierHeroHint")}
								</span>
							</button>
						);
					})}
				</div>
			</div>

			<div>
				<span className="mb-1.5 block text-xs font-bold text-ink">
					{ts("formats")} <span className="font-semibold text-subtle">{ts("formatsHint")}</span>
				</span>
				{isLoadingPresets ? (
					<Loader />
				) : (
					<div className="flex flex-wrap gap-2">
						{(presets ?? []).map((preset) => {
							const isActive = selectedFormatIds.includes(preset.id);

							return (
								<button
									key={preset.id}
									className={classNames(
										"rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
										isActive
											? "border-accent bg-accent-tint text-accent-strong"
											: "border-border bg-white text-subtle hover:bg-surface"
									)}
									type="button"
									onClick={() => toggleFormat(preset.id)}
								>
									{preset.label}{" "}
									<span className="font-normal">
										{preset.width}×{preset.height}
									</span>
								</button>
							);
						})}
					</div>
				)}
				<p className="mt-1.5 text-xs text-subtle">{ts("formatsManagedHint")}</p>
			</div>

			<div className="flex items-center justify-between rounded-card bg-surface px-3 py-2.5">
				<span className="text-xs font-semibold text-subtle">
					{ts("estimatedCost")}{" "}
					{tierModel
						? ts("estimateDetail", {
								variants: tierModel.defaultVariantCount,
								formats: selectedFormatIds.length,
								tier: tierLabel,
							})
						: null}
				</span>
				<span className="text-sm font-bold text-ink">
					{estimate ? formatUsd(estimate.amountUsd) : "—"}
				</span>
			</div>

			{error && <p className="text-xs font-semibold text-danger">{error}</p>}

			<Button
				className="w-full"
				color={ButtonColor.ACTION}
				disabled={isSubmitting || isRunning || !tierModel}
				isLoading={isSubmitting}
				size={ButtonSize.M}
				title={isRunning ? ts("generating") : ts("generate")}
				type="button"
				onClick={handleGenerate}
			/>
			<p className="text-center text-xs text-subtle">{ts("asyncHint")}</p>

			{job && (
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<span className="text-xs font-bold text-ink">
							{ts("results")}{" "}
							<span className="font-semibold text-subtle">{ts("jobLabel", { id: job.id })}</span>
						</span>
						{job.status === MediaJobStatus.SUCCEEDED ? (
							<span className="rounded-full bg-success-light px-2.5 py-1 text-xs font-semibold text-text">
								{ts("done")} · {formatUsd(job.actualCostUsd ?? job.estimatedCostUsd)}
							</span>
						) : job.status === MediaJobStatus.FAILED ? (
							<span className="rounded-full bg-error-light px-2.5 py-1 text-xs font-semibold text-text">
								{ts("failed")}
							</span>
						) : job.status === MediaJobStatus.CANCELLED ? (
							<span className="rounded-full bg-surface px-2.5 py-1 text-xs font-semibold text-subtle">
								{ts("cancelled")}
							</span>
						) : (
							<span className="rounded-full bg-status-running-light px-2.5 py-1 text-xs font-semibold text-warning-ink">
								{ts("running")} · {job.progress}%
							</span>
						)}
					</div>

					{isRunning && (
						<>
							<div className="h-1.5 w-full overflow-hidden rounded-full bg-border-soft">
								<div
									className="h-full rounded-full bg-accent transition-all"
									style={{ width: `${Math.max(job.progress, 5)}%` }}
								/>
							</div>
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								disabled={isCancelling}
								size={ButtonSize.XS}
								title={ts("cancel")}
								type="button"
								onClick={() => cancelJob(job.id)}
							/>
						</>
					)}

					{job.errorMessage && (
						<p className="text-xs font-semibold text-danger">{job.errorMessage}</p>
					)}

					{job.formats.length > 1 && job.assets.length > 0 && (
						<div className="flex flex-wrap gap-2">
							{job.formats.map((format) => {
								const isActive =
									(previewFormatId ?? job.assets[0]?.formatPresetId ?? null) === format.id;

								return (
									<button
										key={format.id}
										className={classNames(
											"rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
											isActive
												? "border-accent bg-accent-tint text-accent-strong"
												: "border-border bg-white text-subtle hover:bg-surface"
										)}
										type="button"
										onClick={() => setPreviewFormatId(format.id)}
									>
										{format.label}
									</button>
								);
							})}
						</div>
					)}

					{variantsForPreview.length > 0 && (
						<div className="grid grid-cols-2 gap-2">
							{variantsForPreview.map((asset) => (
								<div key={asset.id} className="space-y-1.5">
									<button
										className={classNames(
											"block w-full overflow-hidden rounded-card border-2 transition-colors",
											asset.isSelected ? "border-accent" : "border-border-soft"
										)}
										disabled={isSelecting}
										type="button"
										onClick={() => handleSelectVariant(asset.id)}
									>
										<img
											alt={ts("variant", { index: asset.variantIndex + 1 })}
											className="aspect-video w-full object-cover"
											src={asset.fullFilePath}
										/>
									</button>
									<Button
										className="w-full"
										color={asset.isSelected ? ButtonColor.ACTION : ButtonColor.ACTION_SECONDARY}
										disabled={isSelecting}
										size={ButtonSize.XS}
										title={ts("applyVariant", { index: asset.variantIndex + 1 })}
										type="button"
										onClick={() => handleSelectVariant(asset.id)}
									/>
								</div>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};
